import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { AdminPageProps, CurrentUser, Store } from "@/types";
import { Link, router, usePage } from "@inertiajs/react";
import { ComponentProps } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BadgeCheckIcon,
  ChartNoAxesCombinedIcon,
  CheckIcon,
  ChevronsUpDown,
  CreditCardIcon,
  LogOutIcon,
  LucideIcon,
  PlusIcon,
  SettingsIcon,
  SquareArrowOutUpRightIcon,
  StoreIcon,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  analytics_store_path,
  select_plan_path,
  settings_store_path,
  store_collections_path,
  store_path,
  store_products_path,
  store_users_path,
} from "@/lib/routes";
import { getBaseUrl, getStoreDomain, getStoreUrl } from "@/lib/utils";
import usePermissions from "@/hooks/use-permissions";
import useDialog from "@/hooks/use-dialog";
import useTranslations from "@/hooks/use-translations";
import NewStoreDialog from "./new-store-dialog";
import AccountSheet from "./account-sheet";

function StoreSwitcher({
  currentStore,
  stores,
}: {
  currentStore?: Store;
  stores: Store[];
}) {
  const { isMobile } = useSidebar();
  const { can } = usePermissions();
  const { t } = useTranslations("shared.components.app_sidebar");

  const newStoreDialog = useDialog();

  if (!currentStore) {
    return null;
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                {/* <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <activeTeam.logo className="size-4" />
              </div> */}
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {currentStore.name}
                  </span>
                  <span className="truncate text-xs">
                    {getStoreDomain(currentStore.subdomain)}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              align="start"
              side={isMobile ? "bottom" : "right"}
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-muted-foreground text-xs">
                {t("stores_label")}
              </DropdownMenuLabel>
              {stores.map((store) => (
                <DropdownMenuItem key={store.id}>
                  <a href={store_path(store.id)} className="flex-1">
                    {store.name}
                  </a>
                  {currentStore.id === store.id && <CheckIcon />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2 p-2"
                disabled={!can("create_store")}
                onSelect={newStoreDialog.open}
              >
                <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                  <PlusIcon className="size-4" />
                </div>
                <div className="text-muted-foreground font-medium">
                  {t("add_store")}
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <NewStoreDialog
        open={newStoreDialog.isOpen}
        onOpenChange={newStoreDialog.setIsOpen}
      />
    </>
  );
}

function NavUser({ user }: { user: CurrentUser }) {
  const { isMobile } = useSidebar();
  const { t } = useTranslations("shared.components.app_sidebar");
  const accountSheet = useDialog();

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  {/* <AvatarImage src={user.avatar} alt={user.name} /> */}
                  <AvatarFallback className="rounded-lg">
                    {user.email[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.email}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    {/* <AvatarImage src={user.avatar} alt={user.name} /> */}
                    <AvatarFallback className="rounded-lg">
                      {user.email[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{user.email}</span>
                    <span className="truncate text-xs">{user.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onSelect={accountSheet.open}>
                  <BadgeCheckIcon />
                  {t("account")}
                </DropdownMenuItem>
                {!user.plan && (
                  <DropdownMenuItem asChild>
                    <Link
                      href={select_plan_path({ params: { plan: "basic" } })}
                    >
                      <CreditCardIcon />
                      {t("upgrade_plan")}
                    </Link>
                  </DropdownMenuItem>
                )}
                {/* <DropdownMenuItem>
                <Bell />
                Notifications
              </DropdownMenuItem> */}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => router.delete("/session")}>
                <LogOutIcon />
                {t("log_out")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <AccountSheet
        open={accountSheet.isOpen}
        onOpenChange={accountSheet.setIsOpen}
      />
    </>
  );
}

function useSidebarNavMain(): {
  title: string;
  url: typeof store_path | typeof store_products_path;
  icon?: LucideIcon;
  isActive?: boolean;
}[] {
  const { t } = useTranslations("shared.components.app_sidebar");

  return [
    { title: t("dashboard"), url: store_path },
    // { title: "Products", url: store_products_path },
    { title: t("collections"), url: store_collections_path },
    { title: t("users"), url: store_users_path },
  ];
}

function useSidebarNavSecondary(): {
  title: string;
  url: typeof store_path | typeof store_products_path;
  icon?: LucideIcon;
  isActive?: boolean;
}[] {
  const { t } = useTranslations("shared.components.app_sidebar");

  return [
    {
      title: t("analytics"),
      url: analytics_store_path,
      icon: ChartNoAxesCombinedIcon,
    },
    { title: t("settings"), url: settings_store_path, icon: SettingsIcon },
  ];
}

function isLinkActive(url: string): boolean {
  const { url: currentUrl } = usePage();

  return currentUrl === url;
}

export default function AppSidebar(props: ComponentProps<typeof Sidebar>) {
  const { current_store, current_user, stores } =
    usePage<AdminPageProps>().props;
  const { t } = useTranslations("shared.components.app_sidebar");
  const sidebarNavMain = useSidebarNavMain();
  const sidebarNavSecondary = useSidebarNavSecondary();

  if (!current_store) {
    return null;
  }

  return (
    <Sidebar {...props}>
      <SidebarHeader>
        <StoreSwitcher currentStore={current_store} stores={stores} />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {sidebarNavMain.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  isActive={
                    item.isActive ?? isLinkActive(item.url(current_store.id))
                  }
                >
                  <Link href={item.url(current_store.id)}>
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>

        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>
              {sidebarNavSecondary.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={
                      item.isActive ?? isLinkActive(item.url(current_store.id))
                    }
                  >
                    <Link href={item.url(current_store.id)}>
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              <SidebarMenuItem key="external">
                <SidebarMenuButton asChild>
                  <a
                    href={getStoreUrl(current_store.subdomain)}
                    target="_blank"
                  >
                    <StoreIcon />
                    {t("visit_store")}
                  </a>
                </SidebarMenuButton>
                <SidebarMenuBadge>
                  <SquareArrowOutUpRightIcon className="size-4" />
                </SidebarMenuBadge>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        {current_user && <NavUser user={current_user} />}
      </SidebarFooter>
    </Sidebar>
  );
}
